import { useMemo } from "react";
import { useNavigate } from "react-router";

import { Gear } from "components/icons/generated";
import useTypedContext from "hooks/useTypedContext";
import { isSaasDistribution, isSelfHostedDistribution } from "utils/distribution";
import { isFedRampRegion } from "utils/region";
import { AccountContext } from "views/AccountWrapper";
import { pathConfig } from "views/Account/Settings/constants";
import {
  OrganizationAccessTabs,
  OrganizationAccountTabs,
  OrganizationAuthTabs,
  OrganizationSecurityTabs,
} from "views/Account/Settings/types";

import { CommandPaletteActionType, CommandPaletteBucket } from "../types";
import { BUCKET_IDS } from "../constants";

const isSaas = isSaasDistribution();
const isSelfHosted = isSelfHostedDistribution();
const isFedRamp = isFedRampRegion();

type OrganizationSettingsItem = {
  tab:
    | OrganizationAccessTabs
    | OrganizationAuthTabs
    | OrganizationSecurityTabs
    | OrganizationAccountTabs;
  title: string;
  keywords?: string[];
};

const useCommandPaletteOrganizationSettingsBucket = (): CommandPaletteBucket => {
  const navigate = useNavigate();
  const { viewer } = useTypedContext(AccountContext);

  const actions = useMemo(() => {
    if (!viewer.admin) return [];

    // Mirrors the sections of the Organization settings page (Settings.tsx),
    // including the same distribution/feature-flag gating.
    // TODO: the items and their visibility rules are duplicated with Settings.tsx
    // and can drift when settings change. Extract a shared Organization settings
    // routes config that both the settings page and this bucket consume.
    const items: Array<OrganizationSettingsItem | undefined> = [
      { tab: OrganizationAccessTabs.MANAGEMENT_STRATEGY, title: "Management strategy" },
      { tab: OrganizationAccessTabs.SPACES, title: "Spaces" },
      { tab: OrganizationAccessTabs.LOGIN_POLICIES, title: "Login policy" },
      { tab: OrganizationAccessTabs.ROLES, title: "Roles" },
      { tab: OrganizationAccessTabs.USERS, title: "Users" },
      { tab: OrganizationAccessTabs.IDP_GROUP_MAPPING, title: "IdP group mapping" },
      { tab: OrganizationAccessTabs.API_KEYS, title: "API keys" },
      { tab: OrganizationAuthTabs.SSO, title: "Single Sign-on", keywords: ["sso", "saml"] },
      {
        tab: OrganizationAuthTabs.MFA,
        title: "Multi-factor authentication",
        keywords: ["mfa", "2fa"],
      },
      isSaas
        ? { tab: OrganizationSecurityTabs.SECURITY_EMAIL, title: "Security email" }
        : undefined,
      { tab: OrganizationSecurityTabs.SESSIONS, title: "Sessions" },
      { tab: OrganizationSecurityTabs.RUNTIME_SECURITY, title: "Runtime Security" },
      {
        tab: OrganizationSecurityTabs.OIDC_SUBJECT_TEMPLATE,
        title: "OIDC subject template",
        keywords: ["oidc"],
      },
      isSaas && !isFedRamp ? { tab: OrganizationAccountTabs.BILLING, title: "Billing" } : undefined,
      isSaas ? { tab: OrganizationAccountTabs.USAGE, title: "Usage" } : undefined,
      isSaas ? { tab: OrganizationAccountTabs.LIMITS, title: "Limits" } : undefined,
      isSelfHosted
        ? { tab: OrganizationAccountTabs.USAGE_EXPORT, title: "Usage export" }
        : undefined,
      {
        tab: OrganizationAccountTabs.AI,
        title: "Spacelift Intelligence",
        keywords: ["ai", "intelligence"],
      },
      isSaas
        ? { tab: OrganizationAccountTabs.ACCOUNT_DELETION, title: "Account deletion" }
        : undefined,
    ];

    return items
      .filter((item) => item !== undefined)
      .map(({ tab, title, keywords }) => ({
        type: CommandPaletteActionType.SIMPLE,
        id: `go-to-settings-${pathConfig[tab]}`,
        title,
        keywords: ["settings", "organization", "navigate", "go to", ...(keywords ?? [])],
        bucketId: BUCKET_IDS.ORGANIZATION_SETTINGS,
        icon: Gear,
        onAction: () => navigate(`/settings/${pathConfig[tab]}`),
      }));
  }, [viewer.admin, navigate]);

  return useMemo(
    () => ({
      id: BUCKET_IDS.ORGANIZATION_SETTINGS,
      title: "Organization settings",
      order: 4,
      getActions: () => actions,
    }),
    [actions]
  );
};

export default useCommandPaletteOrganizationSettingsBucket;
